import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { updateRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";

interface Person {
  id: string;
  full_name: string;
  gets_every_signing_alert: boolean;
}

/**
 * Who hears about every signed contract, not only their own (ADR 0283).
 *
 * The rep is always told about their own signature, by push and by email.
 * Ticking somebody here tells them about everybody's. Jonathon, 2026-10-15:
 * "lysa gets it regardless".
 */
export function SigningAlertPeople() {
  const { isAdmin } = useSession();
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const people = useAsync<Person[]>(
    async () =>
      unwrap(
        await supabase
          .from("profiles")
          .select("id,full_name,gets_every_signing_alert")
          .eq("active", true)
          .order("sort_order")
          .order("full_name"),
      ) as Person[],
    [],
  );

  async function set(person: Person, value: boolean) {
    setSaving(person.id);
    setError(null);
    try {
      await updateRow("profiles", person.id, {
        gets_every_signing_alert: value,
      });
      await people.reload();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : String(problem));
    } finally {
      setSaving(null);
    }
  }

  if (people.loading) return <div className="stub">{t("Checking…")}</div>;

  return (
    <>
      <div className="rrow" style={{ marginTop: 24 }}>
        <span className="k">{t("Every signed contract")}</span>
      </div>

      <ErrorNote error={error ?? people.error} />

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 520 }}>
        {t(
          "When a customer signs, the rep gets a notification and an email. The people ticked here get both for every job, whoever the rep is.",
        )}
      </p>

      {(people.data ?? []).map((person) => (
        <label key={person.id} className="rrow" style={{ cursor: "pointer" }}>
          <span className="k">{person.full_name}</span>
          <span className="v">
            <input
              type="checkbox"
              aria-label={t("{name} hears about every signed contract", {
                name: person.full_name,
              })}
              checked={person.gets_every_signing_alert}
              disabled={!isAdmin || saving === person.id}
              onChange={(event) => void set(person, event.target.checked)}
            />
          </span>
        </label>
      ))}

      {!isAdmin && (
        <div className="na-hint" style={{ maxWidth: 520 }}>
          {t("Ask an admin to change this.")}
        </div>
      )}
    </>
  );
}
